import moment from 'moment-timezone';
import { AssessmentType } from '../enums/shared.enums';
import { filterValidAssessmentTypes } from './assessmentUtils';
import { PATH_MAIN } from '../routes/paths';
import { InAppNotificationItem } from '../@types/notification.types';

/**
 * The notification type strings the backend sends. Anything not listed here
 * still renders, falling back to the generic bell icon — the backend's
 * `NotificationType` is a free-form string, not a closed union.
 */
export const KnownNotificationType = {
    APPOINTMENT_REMINDER: 'appointment.reminder',
    APPOINTMENT_CANCELLATION: 'appointment.cancellation',
    APPOINTMENT_CONFIRMATION: 'appointment.confirmation',
    APPOINTMENT_RESCHEDULE: 'appointment.reschedule',
    APPOINTMENT_RESCHEDULE_REQUEST: 'appointment.reschedule_request',
    APPOINTMENT_APPROVAL: 'appointment.approval',
    APPOINTMENT_PROVIDER_INITIATED_REQUEST: 'appointment.provider_initiated_request',
    APPOINTMENT_ONGOING: 'appointment.ongoing',
    APPOINTMENT_CANCELLATION_CARE_TEAM: 'appointment.cancellation_care_team',
    APPOINTMENT_PENDING_REMINDER: 'appointment.pending_reminder',
    // Covers provider-late, client-late, and already-started variants, distinguished by `party` in `data`.
    APPOINTMENT_LATE_NOTICE: 'appointment.late_notice',
    ASSESSMENT_INTAKE_ISSUED: 'assessment.intake_issued',
    ASSESSMENT_FOLLOWUP: 'assessment.followup',
    PROVIDER_MISSING_DIAGNOSIS_CODES: 'provider.missing_diagnosis_codes',
    PROVIDER_HIGH_RISK_ALERT: 'provider.high_risk_alert',
    PROVIDER_STALE_APPOINTMENT_CLARIFICATION: 'provider.stale_appointment_clarification',
    GROUP_ACTION_ITEM_OVERDUE: 'group.action_item_overdue',
    GROUP_SESSION_REMINDER: 'group.session_reminder',
    GROUP_SUBSCRIPTION_ACTIVATED: 'group.subscription_activated',
    GROUP_MEMBER_JOINED: 'group.member_joined',
    GROUP_MEMBER_JOINED_OWNER: 'group.member_joined_owner',
    GROUP_SESSION_RESCHEDULED: 'group.session_rescheduled',
    GROUP_ACTIVATED_JOINED: 'group.activated_joined',
    GROUP_ACTIVATED_WAITLISTED: 'group.activated_waitlisted',
    JOURNAL_SHARED: 'journal.shared',
    ADMIN_PUSH: 'admin.push',
    DOCUMENT_PROVIDER_UPLOAD: 'document.provider_upload',
    SUSPENSION_OWED_BALANCE: 'suspension.owed_balance',
    SUSPENSION_OWED_BALANCE_RESOLVED: 'suspension.owed_balance_resolved',
    SUSPENSION_TERMED_COVERAGE_RESOLVED: 'suspension.termed_coverage_resolved',
    SUSPENSION_CLAIM_DENIED_RESOLVED: 'suspension.claim_denied_resolved',
    SUSPENSION_NO_PAYMENT_METHOD: 'suspension.no_payment_method',
    MESSAGE_RECEIVED: 'message.received',
    CLIENT_MINOR_CONSENT_SIGNED: 'client.minor_consent_signed',
    PARTNER_ZOCDOC_SLOT_TAKEN: 'partner.zocdoc_slot_taken',
    PARTNER_ZOCDOC_WELCOME: 'partner.zocdoc_welcome',
    PARTNER_EMPATHIA_WELCOME: 'partner.empathia_welcome',
    PARTNER_COMPSYCH_WELCOME: 'partner.compsych_welcome',
    PARTNER_ZOCDOC_ONBOARDING_REMINDER: 'partner.zocdoc_onboarding_reminder',
    REFERRAL_REWARD_READY: 'referral.reward_ready',
    ACCOUNT_EMAIL_VERIFICATION: 'account.email_verification',
    ACCOUNT_CLIENT_ACCESS_ACTIVATED: 'account.client_access_activated',
} as const;

const DEFAULT_NOTIFICATION_ICON = 'lucide:bell';

const NOTIFICATION_TYPE_ICON: Record<string, string> = {
    [KnownNotificationType.APPOINTMENT_REMINDER]: 'lucide:calendar',
    [KnownNotificationType.APPOINTMENT_CANCELLATION]: 'lucide:calendar',
    [KnownNotificationType.APPOINTMENT_CONFIRMATION]: 'lucide:calendar',
    [KnownNotificationType.APPOINTMENT_RESCHEDULE]: 'lucide:calendar',
    [KnownNotificationType.APPOINTMENT_RESCHEDULE_REQUEST]: 'lucide:calendar',
    [KnownNotificationType.APPOINTMENT_APPROVAL]: 'lucide:calendar',
    [KnownNotificationType.APPOINTMENT_PROVIDER_INITIATED_REQUEST]: 'lucide:calendar',
    [KnownNotificationType.APPOINTMENT_ONGOING]: 'lucide:calendar',
    [KnownNotificationType.APPOINTMENT_CANCELLATION_CARE_TEAM]: 'lucide:calendar',
    [KnownNotificationType.APPOINTMENT_PENDING_REMINDER]: 'lucide:calendar',
    [KnownNotificationType.APPOINTMENT_LATE_NOTICE]: 'lucide:calendar-clock',
    [KnownNotificationType.ASSESSMENT_INTAKE_ISSUED]: 'lucide:clipboard-list',
    [KnownNotificationType.ASSESSMENT_FOLLOWUP]: 'lucide:clipboard-list',
    [KnownNotificationType.PROVIDER_MISSING_DIAGNOSIS_CODES]: 'lucide:stethoscope',
    [KnownNotificationType.PROVIDER_HIGH_RISK_ALERT]: 'lucide:stethoscope',
    [KnownNotificationType.PROVIDER_STALE_APPOINTMENT_CLARIFICATION]: 'lucide:stethoscope',
    [KnownNotificationType.GROUP_ACTION_ITEM_OVERDUE]: 'lucide:users',
    [KnownNotificationType.GROUP_SESSION_REMINDER]: 'lucide:users',
    [KnownNotificationType.GROUP_SUBSCRIPTION_ACTIVATED]: 'lucide:users',
    [KnownNotificationType.GROUP_MEMBER_JOINED]: 'lucide:users',
    [KnownNotificationType.GROUP_MEMBER_JOINED_OWNER]: 'lucide:users',
    [KnownNotificationType.GROUP_SESSION_RESCHEDULED]: 'lucide:users',
    [KnownNotificationType.GROUP_ACTIVATED_JOINED]: 'lucide:users',
    [KnownNotificationType.GROUP_ACTIVATED_WAITLISTED]: 'lucide:users',
    [KnownNotificationType.JOURNAL_SHARED]: 'lucide:book-open',
    [KnownNotificationType.ADMIN_PUSH]: 'lucide:megaphone',
    [KnownNotificationType.DOCUMENT_PROVIDER_UPLOAD]: 'lucide:file-text',
    [KnownNotificationType.SUSPENSION_OWED_BALANCE]: 'lucide:credit-card',
    [KnownNotificationType.SUSPENSION_OWED_BALANCE_RESOLVED]: 'lucide:credit-card',
    [KnownNotificationType.SUSPENSION_TERMED_COVERAGE_RESOLVED]: 'lucide:credit-card',
    [KnownNotificationType.SUSPENSION_CLAIM_DENIED_RESOLVED]: 'lucide:credit-card',
    [KnownNotificationType.SUSPENSION_NO_PAYMENT_METHOD]: 'lucide:credit-card',
    [KnownNotificationType.MESSAGE_RECEIVED]: 'lucide:message-circle',
    [KnownNotificationType.CLIENT_MINOR_CONSENT_SIGNED]: 'lucide:file-signature',
    [KnownNotificationType.PARTNER_ZOCDOC_SLOT_TAKEN]: 'lucide:handshake',
    [KnownNotificationType.PARTNER_ZOCDOC_WELCOME]: 'lucide:handshake',
    [KnownNotificationType.PARTNER_EMPATHIA_WELCOME]: 'lucide:handshake',
    [KnownNotificationType.PARTNER_COMPSYCH_WELCOME]: 'lucide:handshake',
    [KnownNotificationType.PARTNER_ZOCDOC_ONBOARDING_REMINDER]: 'lucide:handshake',
    [KnownNotificationType.REFERRAL_REWARD_READY]: 'lucide:gift',
    [KnownNotificationType.ACCOUNT_EMAIL_VERIFICATION]: 'lucide:user-check',
    [KnownNotificationType.ACCOUNT_CLIENT_ACCESS_ACTIVATED]: 'lucide:user-check',
};

/** Resolves the Iconify glyph for a notification type. */
export const getNotificationIcon = (type: string): string =>
    NOTIFICATION_TYPE_ICON[type] ?? DEFAULT_NOTIFICATION_ICON;

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * Formats a notification timestamp using a four-tier ladder:
 *   under a minute  -> "Just now"
 *   under an hour   -> "3 min ago"
 *   earlier today   -> "5 hr ago"
 *   yesterday       -> "Yesterday, 12:30 PM"
 *   older           -> "Sep 22, 7:00 AM"
 *
 * Tiers are calendar-aware, not purely elapsed-time, so something five hours
 * old that fell on the previous calendar day reads "Yesterday" rather than
 * "5 hr ago".
 */
export const formatNotificationTimestamp = (
    value: string,
    translate: TranslateFn,
    now?: moment.Moment
): string => {
    const target = moment(value);
    if (!target.isValid()) return '';

    const reference = now ?? moment();
    const minutes = reference.diff(target, 'minutes');

    if (minutes < 1) return translate('layouts.notificationsPopover.timestamp.justNow');
    if (minutes < 60)
        return translate('layouts.notificationsPopover.timestamp.minutesAgo', { count: minutes });

    if (target.isSame(reference, 'day')) {
        const hours = reference.diff(target, 'hours');
        return translate('layouts.notificationsPopover.timestamp.hoursAgo', { count: hours });
    }

    if (target.isSame(reference.clone().subtract(1, 'day'), 'day')) {
        return translate('layouts.notificationsPopover.timestamp.yesterday', {
            time: target.format('h:mm A'),
        });
    }

    return target.format('MMM D, h:mm A');
};

/** The assessment types a notification's `data` payload names, in the order named. */
export const parseNotificationAssessmentTypes = (
    data?: string | null
): AssessmentType[] => {
    if (typeof data !== 'string') return [];

    let parsed: unknown;
    try {
        parsed = JSON.parse(data);
    } catch {
        return [];
    }

    if (typeof parsed !== 'object' || parsed === null) return [];

    const named = (parsed as Record<string, unknown>).assessmentsToTake;
    if (!Array.isArray(named)) return [];

    return filterValidAssessmentTypes(named);
};

/** A single numeric field out of a notification's `data` JSON string, or `null` if absent/malformed. */
const parseNotificationNumberField = (
    data: string | null | undefined,
    field: string
): number | null => {
    if (typeof data !== 'string') return null;

    let parsed: unknown;
    try {
        parsed = JSON.parse(data);
    } catch {
        return null;
    }

    if (typeof parsed !== 'object' || parsed === null) return null;

    const value = (parsed as Record<string, unknown>)[field];
    return typeof value === 'number' ? value : null;
};

export interface NotificationDestination {
    pathname: string;
    state?: Record<string, unknown>;
}

/** Types whose `data` carries `appointmentId` and land on the appointment details screen. */
const APPOINTMENT_DETAIL_TYPES = new Set<string>([
    KnownNotificationType.APPOINTMENT_REMINDER,
    KnownNotificationType.APPOINTMENT_CANCELLATION,
    KnownNotificationType.APPOINTMENT_CONFIRMATION,
    KnownNotificationType.APPOINTMENT_RESCHEDULE,
    KnownNotificationType.APPOINTMENT_RESCHEDULE_REQUEST,
    KnownNotificationType.APPOINTMENT_PROVIDER_INITIATED_REQUEST,
    KnownNotificationType.APPOINTMENT_ONGOING,
    KnownNotificationType.APPOINTMENT_CANCELLATION_CARE_TEAM,
    KnownNotificationType.APPOINTMENT_PENDING_REMINDER,
    KnownNotificationType.APPOINTMENT_LATE_NOTICE,
]);

/** Types whose `data` carries `groupId` and land on the group details screen. */
const GROUP_DETAIL_TYPES = new Set<string>([
    KnownNotificationType.GROUP_MEMBER_JOINED,
    KnownNotificationType.GROUP_MEMBER_JOINED_OWNER,
    KnownNotificationType.GROUP_SESSION_RESCHEDULED,
    KnownNotificationType.GROUP_ACTIVATED_JOINED,
    KnownNotificationType.GROUP_ACTIVATED_WAITLISTED,
]);

/** Types with no per-record reference — the type alone identifies the billing/subscription screen. */
const BILLING_REDIRECT_TYPES = new Set<string>([
    KnownNotificationType.GROUP_SUBSCRIPTION_ACTIVATED,
    KnownNotificationType.SUSPENSION_OWED_BALANCE,
    KnownNotificationType.SUSPENSION_OWED_BALANCE_RESOLVED,
    KnownNotificationType.SUSPENSION_TERMED_COVERAGE_RESOLVED,
    KnownNotificationType.SUSPENSION_CLAIM_DENIED_RESOLVED,
    KnownNotificationType.SUSPENSION_NO_PAYMENT_METHOD,
]);

/**
 * Resolves where a notification tap should navigate, or `null` when the type
 * has no addressable screen. The backend's `action` deep-link discriminant is
 * always null today (its `NotificationEvent` never sets it), so `type` + `data`
 * is the only routable contract the client has — see the notification docs'
 * "How to read Type: and Recipients:" section.
 *
 * Types with no `referenceSnapshot` payload (most provider-ops, partner-welcome
 * and account-lifecycle notifications) fall through to `null`: there is
 * nothing to address them by, and no dedicated screen to land on regardless.
 */
export const resolveNotificationDestination = (
    item: Pick<InAppNotificationItem, 'type' | 'data'>
): NotificationDestination | null => {
    const { type, data } = item;

    if (
        type === KnownNotificationType.ASSESSMENT_INTAKE_ISSUED ||
        type === KnownNotificationType.ASSESSMENT_FOLLOWUP
    ) {
        const assessmentsToTake = parseNotificationAssessmentTypes(data);
        if (assessmentsToTake.length > 0) {
            return { pathname: PATH_MAIN.general.assessments, state: { assessmentsToTake } };
        }

        // ASSESSMENT_FOLLOWUP also carries appointmentId; fall back to it once
        // there is nothing outstanding left to assign.
        const appointmentId = parseNotificationNumberField(data, 'appointmentId');
        return appointmentId != null
            ? { pathname: `${PATH_MAIN.general.appointmentDetails}/${appointmentId}` }
            : null;
    }

    if (APPOINTMENT_DETAIL_TYPES.has(type)) {
        const appointmentId = parseNotificationNumberField(data, 'appointmentId');
        return appointmentId != null
            ? { pathname: `${PATH_MAIN.general.appointmentDetails}/${appointmentId}` }
            : null;
    }

    if (GROUP_DETAIL_TYPES.has(type)) {
        const groupId = parseNotificationNumberField(data, 'groupId');
        return groupId != null ? { pathname: `${PATH_MAIN.general.group}/${groupId}` } : null;
    }

    if (BILLING_REDIRECT_TYPES.has(type)) {
        return { pathname: PATH_MAIN.billingAndSubscription };
    }

    if (type === KnownNotificationType.REFERRAL_REWARD_READY) {
        return { pathname: PATH_MAIN.general.careCircle };
    }

    if (type === KnownNotificationType.MESSAGE_RECEIVED) {
        const conversationId = parseNotificationNumberField(data, 'conversationId');
        return {
            pathname: PATH_MAIN.general.messages,
            ...(conversationId != null ? { state: { conversationId } } : {}),
        };
    }

    return null;
};
